import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import {
	Badge,
	Card,
	CardBody,
	CardHeader,
	HStack,
	Icon,
	Stack,
	Table,
	TableContainer,
	Tbody,
	Td,
	Text,
	Th,
	Thead,
	Tr,
	useColorModeValue,
	VStack,
} from "@chakra-ui/react";
import { FiArchive, FiHome } from "react-icons/fi";

import useDebounce from "../../Components/Hooks/useDebounce";
import Loader from "../../Components/Loader/Loader";
import TablePagination from "../../Components/Pagination/TablePagination";
import ArchivedPropertiesTableRow from "../../Components/TableRows/ArchivedPropertiesTableRow";
import PropertiesTableRow from "../../Components/TableRows/PropertiesTableRow";
import { selectActiveProperties, selectArchivedProperties } from "../../redux/user/userSlice";
import { getAdministrationStatus, getFinancialStatementStatus, getLeaseStatus, getPropertyStatusText, removeAccents } from "../../utils/utils";
import { getPropertyAddress } from "../../utils/verifier";

import NewPropertyModal from "./NewPropertyModal";
import SearchBox from "./SearchBox";

export default function Properties() {
	const { t } = useTranslation();
	const textColor = useColorModeValue("gray.700", "white");
	const bgCard = useColorModeValue("white", "gray.700");
	const [loading, setLoading] = useState(true);
	const properties = useSelector(selectActiveProperties);
	const archivedProperties = useSelector(selectArchivedProperties);
	const [query, setquery] = useState("");
	const debouncedValue = removeAccents(useDebounce(query, 500));

	useEffect(() => {
		setLoading(false);
	}, []);

	const handleSearch = (query) => {
		setquery(query);
	};

	const propertiesFilterFunction = (p) => {
		const address = getPropertyAddress(p);
		const queryValue = debouncedValue.toLowerCase().trim();
		const association = p.association;
		return (
			p.id?.toLowerCase() === queryValue ||
			removeAccents(address)?.toLowerCase()?.includes(queryValue) ||
			removeAccents(p.owner.name)?.toLowerCase()?.includes(queryValue) ||
			removeAccents(p.owner.email)?.toLowerCase()?.includes(queryValue) ||
			p.tenants?.find((t) => removeAccents(t.name)?.toLowerCase()?.includes(queryValue)) ||
			p.tenants?.find((t) => removeAccents(t.email)?.toLowerCase()?.includes(queryValue)) ||
			p.formerTenants?.find((t) => removeAccents(t.name)?.toLowerCase()?.includes(queryValue)) ||
			p.formerTenants?.find((t) => removeAccents(t.email)?.toLowerCase()?.includes(queryValue)) ||
			p.contacts?.find((t) => removeAccents(t.name)?.toLowerCase()?.includes(queryValue)) ||
			p.utilities.gas?.paymentCode?.toLowerCase()?.includes(queryValue) ||
			p.utilities.gas?.clientCode?.toLowerCase()?.includes(queryValue) ||
			p.utilities.water?.nlcCode?.toLowerCase()?.includes(queryValue) ||
			p.utilities.water?.atmCode?.toLowerCase()?.includes(queryValue) ||
			getPropertyStatusText(p.admStatus)?.toLowerCase()?.includes(queryValue) ||
			getPropertyStatusText(p.leaseStatus)?.toLowerCase()?.includes(queryValue) ||
			getPropertyStatusText(p.financialStatementStatus)?.toLowerCase()?.includes(queryValue) ||
			association?.name?.toLowerCase()?.includes(queryValue) ||
			association?.administratorName?.toLowerCase()?.includes(queryValue) ||
			association?.email?.toLowerCase()?.includes(queryValue)
		);
	};

	// Active properties pagination
	const ITEMS_PER_PAGE = 10;
	const [currentPage, setCurrentPage] = useState(1);
	const [archivedCurrentPage, setArchivedCurrentPage] = useState(1);

	// Calculate pagination values for active properties
	const filteredProperties = properties
		.map((p) => {
			return {
				...p,
				admStatus: getAdministrationStatus(p),
				leaseStatus: getLeaseStatus(p),
				financialStatementStatus: getFinancialStatementStatus(p),
			};
		})
		?.filter(propertiesFilterFunction);

	const totalItems = filteredProperties.length;
	const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
	const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
	const endIndex = startIndex + ITEMS_PER_PAGE;
	const paginatedProperties = filteredProperties.slice(startIndex, endIndex);

	// Calculate pagination values for archived properties
	const totalArchivedItems = archivedProperties.length;
	const totalArchivedPages = Math.ceil(totalArchivedItems / ITEMS_PER_PAGE);
	const archivedStartIndex = (archivedCurrentPage - 1) * ITEMS_PER_PAGE;
	const archivedEndIndex = archivedStartIndex + ITEMS_PER_PAGE;
	const paginatedArchivedProperties = archivedProperties.slice(archivedStartIndex, archivedEndIndex);

	const occupiedCount = properties.filter((p) => p.tenants.filter((t) => !t.former).length > 0 || p.specs.apartmentFeatures.type === "garage").length;
	const emptyCount = properties.filter((p) => p.tenants.filter((t) => !t.former).length === 0 && p.specs.apartmentFeatures.type !== "garage").length;

	return (
		<Stack direction="column" spacing={6}>
			<Card>
				<CardHeader>
					<HStack spacing={3} justify="space-between">
						<HStack spacing={3}>
							<Icon as={FiHome} w={6} h={6} color="blue.500" />
							<Text fontSize="2xl" color={textColor} fontWeight="bold">
								{t("properties.properties")}
							</Text>
						</HStack>
						<HStack spacing={4}>
							<HStack spacing={2} px={4} py={2} bg={useColorModeValue("gray.50", "gray.700")} rounded="md">
								<VStack align="start" spacing={0}>
									<HStack
										spacing={3}
										divider={
											<Text color="gray.500" whiteSpace="pre">
												{" "}
												/{" "}
											</Text>
										}
									>
										<HStack spacing={2}>
											<Badge colorScheme="green">{occupiedCount}</Badge>
											<Text fontSize="sm">{t("properties.occupied")}</Text>
										</HStack>
										<HStack spacing={2}>
											<Badge colorScheme="blue">{emptyCount}</Badge>
											<Text fontSize="sm">{t("properties.empty")}</Text>
										</HStack>
									</HStack>
								</VStack>
							</HStack>
							<SearchBox
								handleSearch={handleSearch}
								searchOptions={[
									{ label: "Address", description: t("properties.searchAddress") },
									{ label: "Owner", description: t("properties.searchOwner") },
									{ label: "Tenant", description: t("properties.searchTenant") },
									{ label: "Former Tenant", description: t("properties.searchFormerTenant") },
									{ label: "Contact", description: t("properties.searchContact") },
									{ label: "Gas Payment Code", description: t("properties.searchGasPayment") },
									{ label: "Gas Client Code", description: t("properties.searchGasClient") },
									{ label: "Water NLC Code", description: t("properties.searchWaterNLC") },
									{ label: "Water ATM Code", description: t("properties.searchWaterATM") },
									{ label: "Administration Status", description: t("properties.searchAdminStatus") },
									{ label: "Lease Status", description: t("properties.searchLeaseStatus") },
									{ label: "Statement Status", description: t("properties.searchStatementStatus") },
									{ label: "Association", description: t("properties.searchAssociation") },
									{ label: "Association Administrator", description: t("properties.searchAssociationAdmin") },
									{ label: "Association Email", description: t("properties.searchAssociationEmail") },
								]}
							/>
						</HStack>
					</HStack>
				</CardHeader>
				<CardBody>
					<VStack spacing={4}>
						<HStack w="100%">
							<NewPropertyModal />
						</HStack>
						{loading ? (
							<Loader text={t("misc.loading")} />
						) : (
							<TableContainer w="100%">
								<Table variant="simple" size="small">
									<Thead position="sticky" top={0} bg={bgCard}>
										<Tr>
											<Th pl="0px">{t("table.address")}</Th>
											<Th>{t("table.owner")}</Th>
											<Th textAlign={"center"}>{t("table.status")}</Th>
											<Th>{t("table.rent")}</Th>
											<Th>{t("table.tenants")}</Th>
											<Th></Th>
										</Tr>
										{/* <Tr>
											<Th></Th>
											<Th></Th>
											<Th>{t("table.administration")}</Th>
											<Th>{t("table.lease")}</Th>
											<Th>{t("table.statements")}</Th>
											<Th></Th>
											<Th></Th>
											<Th></Th>
										</Tr> */}
									</Thead>
									<Tbody>
										{paginatedProperties.map((row, index) => (
											<PropertiesTableRow key={index} row={row} />
										))}
										{filteredProperties.length === 0 && (
											<Tr>
												<Td colSpan={8}>
													<Text color="gray.500" textAlign="center">
														{t("table.noData")}
													</Text>
												</Td>
											</Tr>
										)}
									</Tbody>
								</Table>
							</TableContainer>
						)}
						<TablePagination
							currentPage={currentPage}
							totalPages={totalPages}
							totalItems={totalItems}
							onPageChange={setCurrentPage}
							itemsName={t("properties.properties")}
						/>
					</VStack>
				</CardBody>
			</Card>

			<Card>
				<CardHeader>
					<HStack spacing={3}>
						<Icon as={FiArchive} w={6} h={6} color="gray.500" />
						<Text fontSize="xl" color={textColor} fontWeight="bold">
							{t("properties.archivedProperties")}
						</Text>
					</HStack>
				</CardHeader>
				<CardBody>
					<VStack spacing={4}>
						{loading ? (
							<Loader text={t("misc.loading")} />
						) : (
							<TableContainer w="100%">
								<Table variant="simple" size="sm">
									<Thead position="sticky" top={0} bg={bgCard}>
										<Tr>
											<Th>{t("table.address")}</Th>
											<Th>{t("table.owner")}</Th>
											<Th></Th>
										</Tr>
									</Thead>
									<Tbody>
										{paginatedArchivedProperties.map((row, index) => (
											<ArchivedPropertiesTableRow key={index} row={row} />
										))}
										{archivedProperties.length === 0 && (
											<Tr>
												<Td colSpan={3}>
													<Text color="gray.500" textAlign="center">
														{t("table.noData")}
													</Text>
												</Td>
											</Tr>
										)}
									</Tbody>
								</Table>
							</TableContainer>
						)}
						{archivedProperties.length > 0 && (
							<TablePagination
								currentPage={archivedCurrentPage}
								totalPages={totalArchivedPages}
								totalItems={totalArchivedItems}
								onPageChange={setArchivedCurrentPage}
								itemsName={t("properties.archivedProperties")}
							/>
						)}
					</VStack>
				</CardBody>
			</Card>
		</Stack>
	);
}
